// Bọc 5 loại prompt đang dùng trong CLI: giá trị có sẵn thì dùng, không có mà
// đang non-interactive thì ném `MissingFlagError`, còn lại mới hỏi người dùng.
//
// VÌ SAO BỌC THAY VÌ SỬA TỪNG CHỖ: 65 lời gọi prompt nằm rải 21 file. Mỗi nơi tự
// viết `if (nonInteractive) throw ...` thì chắc chắn có chỗ viết thiếu, và AC
// "không gọi @inquirer/prompts lần nào" không kiểm được. Qua một cửa duy nhất
// thì test mock `@inquirer/prompts` và assert 0 lời gọi là đủ chứng minh.
//
// ⚠️ Import tĩnh 5 hàm inquirer ở đây là CỐ Ý: nhờ vậy test mock được cả 5 và
// chứng minh chế độ non-interactive không chạm vào cái nào.

import { checkbox, confirm, input, password, select } from "@inquirer/prompts";
import { isNonInteractive, MissingFlagError } from "./non-interactive-context.js";

/** Mẩu thông tin để dựng `MissingFlagError` khi không có giá trị mà cũng không được hỏi. */
export interface FlagSpec {
  /** Cờ điền giá trị này, ví dụ `--ai-provider`. */
  flag: string;
  /** Lệnh đang chạy, ví dụ `ai setup`. */
  command: string;
  /** Gợi ý điền gì, ví dụ `--ai-provider anthropic|llmlite`. */
  hint: string;
}

/**
 * Giá trị coi như ĐÃ CÓ khi khác `undefined`/`null`/chuỗi rỗng.
 *
 * `false` và `[]` là giá trị hợp lệ (người dùng chủ động chọn "không") nên
 * KHÔNG rơi vào nhánh thiếu — đây là chỗ dễ sai nhất của kiểu kiểm `if (!value)`.
 */
function isProvided<T>(value: T | undefined | null): value is T {
  return value !== undefined && value !== null && value !== "";
}

/**
 * Lõi chung: có giá trị → trả; non-interactive → ném; còn lại → hỏi.
 *
 * `ask` là hàm chứ không phải giá trị: nhờ vậy nhánh non-interactive không bao
 * giờ chạm tới `@inquirer/prompts`, kể cả để dựng tham số.
 */
async function resolveOrPrompt<T>(
  provided: T | undefined | null,
  spec: FlagSpec,
  ask: () => Promise<T>,
): Promise<T> {
  if (isProvided(provided)) return provided;
  if (isNonInteractive()) {
    throw new MissingFlagError({
      missingFlag: spec.flag,
      command: spec.command,
      hint: spec.hint,
    });
  }
  return await ask();
}

/** `select` — chọn 1 trong nhiều. */
export async function selectOrFlag<T>(
  provided: T | undefined,
  spec: FlagSpec,
  config: Parameters<typeof select<T>>[0],
): Promise<T> {
  return await resolveOrPrompt(provided, spec, () => select<T>(config));
}

/** `input` — nhập chuỗi tự do. */
export async function inputOrFlag(
  provided: string | undefined,
  spec: FlagSpec,
  config: Parameters<typeof input>[0],
): Promise<string> {
  return await resolveOrPrompt(provided, spec, () => input(config));
}

/**
 * `password` — bí mật.
 *
 * ⚠️ `provided` ở đây phải đến từ stdin (`read-secret-from-stdin`), KHÔNG từ
 * `argv`: argv lộ qua `ps`, qua log của process manager, qua history của shell.
 */
export async function passwordOrFlag(
  provided: string | undefined,
  spec: FlagSpec,
  config: Parameters<typeof password>[0],
): Promise<string> {
  return await resolveOrPrompt(provided, spec, () => password(config));
}

/** `confirm` — có/không. `provided === false` là câu trả lời hợp lệ, không phải thiếu. */
export async function confirmOrFlag(
  provided: boolean | undefined,
  spec: FlagSpec,
  config: Parameters<typeof confirm>[0],
): Promise<boolean> {
  return await resolveOrPrompt(provided, spec, () => confirm(config));
}

/** `checkbox` — chọn nhiều. Mảng rỗng là "không chọn gì", vẫn tính là đã trả lời. */
export async function checkboxOrFlag<T>(
  provided: T[] | undefined,
  spec: FlagSpec,
  config: Parameters<typeof checkbox<T>>[0],
): Promise<T[]> {
  return await resolveOrPrompt(provided, spec, () => checkbox<T>(config));
}
